import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const keyStats = [
  { value: '25+', label: 'Jahre Erfahrung' },
  { value: '500+', label: 'Zufriedene Kunden' },
  { value: '10.000+', label: 'Abgeschlossene Aufträge' },
];

const serviceIcons = [
  { icon: 'ri-paint-brush-line', title: 'Siebdruck' },
  { icon: 'ri-profile-line', title: 'Geschäftsausstattung' },
  { icon: 'ri-pages-line', title: 'Flyer & Broschüren' },
  { icon: 'ri-image-line', title: 'Plakate & Werbemittel' },
  { icon: 'ri-sticky-note-line', title: 'Aufkleber & Etiketten' },
  { icon: 'ri-brush-line', title: 'Sonderanfertigungen' },
];

export default function Home() {
  useEffect(() => {
    document.title = 'Druckerei Rahmede | Siebdruck & Druckerei in Schalksmühle-Heedfeld';
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20artistic%20modern%20printing%20workshop%20interior%20with%20vintage%20printing%20press%20machinery%20ink%20rollers%20and%20textured%20paper%20stacks%20soft%20warm%20amber%20lighting%20shallow%20depth%20of%20field%20editorial%20photography%20style%20rich%20terracotta%20and%20cream%20tones%20elegant%20industrial%20atmosphere&width=1600&height=900&seq=hero-rahmede-01&orientation=landscape"
          alt="Druckerei Rahmede — Ihr Druck- und Siebdruckbetrieb"
          title="Druckerei Rahmede — Druckerei und Siebdruck in Schalksmühle-Heedfeld"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/50"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-accent-400 text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4 font-medium">
              Druckerei & Siebdruck · Schalksmühle-Heedfeld
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
              Tradition trifft<br className="md:hidden" /> Präzision
            </h1>
            <p className="text-white/80 text-sm md:text-lg max-w-xl mx-auto leading-relaxed">
              Hochwertige Druckerzeugnisse mit persönlichem Service. Seit vielen Jahren Ihr verlässlicher Partner für Siebdruck, Geschäftsausstattung und individuelle Drucklösungen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6 md:mt-8">
              <Link
                to="/kontakt"
                className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                Kostenloses Angebot anfordern
              </Link>
              <Link
                to="/leistungen"
                className="w-full sm:w-auto px-6 py-3 bg-white/15 text-white text-sm font-medium rounded-md hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                Unsere Leistungen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + Key Stats */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-accent-500 text-xs tracking-[0.2em] uppercase font-medium mb-3">
            Willkommen bei Rahmede
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
            Druckqualität, der Sie <span className="text-primary-600">vertrauen</span> können
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-10">
            Als familiennaher Druck- und Siebdruckbetrieb aus Schalksmühle-Heedfeld verbinden wir handwerkliche Präzision mit moderner Drucktechnik. Ob kleine Auflagen oder größere Projekte — wir begleiten Sie von der ersten Idee bis zum fertigen Produkt.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {keyStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <span className="font-heading text-3xl md:text-4xl font-bold text-primary-600">{stat.value}</span>
                <p className="text-xs text-foreground-500 mt-1.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unsere Leistungen — Kompakt */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-accent-500 text-xs tracking-[0.2em] uppercase font-medium mb-3">
              Unsere Leistungen
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Alles aus einer Hand
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {serviceIcons.map((service) => (
              <Link
                key={service.title}
                to="/leistungen"
                className="flex flex-col items-center gap-3 p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/50 hover:border-primary-300 transition-colors cursor-pointer group"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100/60 group-hover:bg-accent-200/70 transition-colors">
                  <i className={`${service.icon} text-xl text-accent-600`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 text-center leading-tight">
                  {service.title}
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <Link
              to="/leistungen"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              Alle Leistungen im Detail
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Warum Rahmede? — 3 Kern-USPs */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-accent-500 text-xs tracking-[0.2em] uppercase font-medium mb-3">
              Warum Rahmede?
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Was uns auszeichnet
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center p-6 rounded-lg border border-background-200/50">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100/60 mx-auto mb-4">
                <i className="ri-user-heart-line text-xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                Persönliche Beratung
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Bei uns sprechen Sie mit echten Menschen. Wir nehmen uns Zeit für Ihre Wünsche und finden die beste Lösung.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg border border-background-200/50">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100/60 mx-auto mb-4">
                <i className="ri-focus-3-line text-xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                Präzise Verarbeitung
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Handwerkliche Präzision und moderne Drucktechnik garantieren makellose Ergebnisse.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg border border-background-200/50">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100/60 mx-auto mb-4">
                <i className="ri-timer-line text-xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                Termingerechte Lieferung
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Verlässlichkeit ist unser Versprechen — Ihre Drucksachen pünktlich wie vereinbart.
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/ueber-uns"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              Mehr über uns erfahren
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Kontakt CTA */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-primary-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight mb-4">
            Bereit für Ihr nächstes Projekt?
          </h2>
          <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-6">
            Kontaktieren Sie uns für ein unverbindliches Angebot. Wir beraten Sie gerne persönlich und finden die passende Lösung für Ihre Druckvorhaben.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto px-6 py-3 bg-white text-primary-600 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Jetzt Kontakt aufnehmen
            </Link>
            <a
              href="tel:+4923514327721"
              className="w-full sm:w-auto px-6 py-3 bg-white/15 text-white text-sm font-medium rounded-md hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              02351 4327721
            </a>
          </div>
        </div>
      </section>
    </>
  );
}